import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useFavorites, useCompare } from "../context/FavoritesCompareContext";
import ProductCard from "../components/ProductCard";
import { Heart, GitCompare, Trash2, ShoppingCart, Star } from "lucide-react";
import { productImageUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";

export const FavoritesPage = () => {
  const { ids } = useFavorites();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (ids.length === 0) { setProducts([]); return; }
    api.get("/products", { params: { limit: 500 } }).then(({ data }) => setProducts(data.filter(p => ids.includes(p.product_id))));
  }, [ids]);
  return (
    <div className="je-container py-10" data-testid="favorites-page">
      <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3" style={{fontFamily:"Outfit"}}><Heart className="w-8 h-8 text-[#E3000F] fill-current" /> Mes favoris</h1>
      <p className="text-gray-500 mt-1">{ids.length} produit(s) sauvegardé(s)</p>
      {ids.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-xl mt-6"><p className="text-gray-500">Vous n'avez pas encore de favoris.</p><Link to="/products" className="je-btn-blue inline-block mt-4">Découvrir les produits</Link></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mt-8">
          {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      )}
    </div>
  );
};

export const ComparePage = () => {
  const { ids, toggle, clear } = useCompare();
  const { addItem } = useCart();
  const { format: formatGdes } = useCurrency();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (ids.length === 0) { setProducts([]); return; }
    api.get("/products", { params: { limit: 500 } }).then(({ data }) => setProducts(data.filter(p => ids.includes(p.product_id))));
  }, [ids]);
  const rows = [
    { label: "Prix", render: (p) => formatGdes(p.price) },
    { label: "Marque", render: (p) => p.brand || "—" },
    { label: "Catégorie", render: (p) => p.category },
    { label: "Stock", render: (p) => p.stock },
    { label: "Note", render: (p) => <span className="inline-flex items-center gap-1">{p.rating}<Star className="w-3 h-3 je-star fill-current" /></span> },
    { label: "Description", render: (p) => <span className="text-xs text-gray-600 line-clamp-3">{p.description}</span> },
  ];
  return (
    <div className="je-container py-10" data-testid="compare-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3" style={{fontFamily:"Outfit"}}><GitCompare className="w-8 h-8 text-[#0052CC]" /> Comparateur</h1>
        {ids.length > 0 && <button onClick={clear} className="text-sm text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Vider</button>}
      </div>
      <p className="text-gray-500 mt-1">{ids.length}/4 produits • Comparez côte à côte</p>
      {ids.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-xl mt-6"><p className="text-gray-500">Aucun produit à comparer. Cliquez sur l'icône comparer sur les fiches produit.</p></div>
      ) : (
        <div className="mt-8 overflow-x-auto">
          <table className="min-w-full bg-white border rounded-xl">
            <thead>
              <tr>
                <th className="p-3"></th>
                {products.map((p) => (
                  <th key={p.product_id} className="p-3 min-w-[220px] align-top">
                    <div className="relative">
                      <button onClick={() => toggle(p.product_id)} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">×</button>
                      <div className="w-full" style={{ height: "160px" }}>
                        <img src={productImageUrl(p.images?.[0])} alt={p.name} className="block w-full h-full object-cover rounded-lg" />
                      </div>
                      <Link to={`/products/${p.product_id}`} className="block font-semibold text-sm mt-2 hover:text-[#0052CC] line-clamp-2">{p.name}</Link>
                      <button onClick={() => addItem(p, 1)} className="je-btn-primary text-xs mt-2 w-full inline-flex items-center justify-center gap-1"><ShoppingCart className="w-3 h-3" /> Ajouter</button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-t">
                  <td className="p-3 font-semibold text-sm text-gray-700 bg-gray-50">{r.label}</td>
                  {products.map((p) => <td key={p.product_id} className="p-3 text-sm">{r.render(p)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
